/**
 * Mixed by Nasrin — <gallery-dots>
 * -----------------------------------------------------------------------
 * Pagination dots for the mobile PDP media slider. Reads scroll position
 * off the existing Dawn slider; does not touch SliderComponent or
 * MediaGallery, so variant swaps and the lightbox keep working as before.
 *
 * Usage: place inside <slider-component>, after the <ul class="slider">.
 *   <gallery-dots class="quick-add-hidden"></gallery-dots>
 *
 * 2026-08-15: active slide is resolved by nearest offsetLeft rather than
 * dividing scrollLeft by the container width. Slides are narrower than the
 * track whenever --mixed-peek is non-zero, so the division approach drifts
 * and the wrong dot lights up partway through the gallery. offsetLeft is
 * correct for any slide width, peek, or gap.
 *
 * 2026-09-11: dots rebuild on slider resize and on slide add/remove/reorder
 * instead of initialising once. A one-shot init measured zero-width slides
 * after rotation and went permanently hidden, and variant swaps that reorder
 * media left the dots pointing at stale slide references.
 */
(function () {
  if (customElements.get('gallery-dots')) return;

  class GalleryDots extends HTMLElement {
    connectedCallback() {
      // Wait a frame so layout has settled and hidden variant slides
      // report a real width.
      requestAnimationFrame(() => this.init());
    }

    init() {
      const component = this.closest('slider-component');
      this.slider = component && component.querySelector('ul.slider');
      if (!this.slider) return;

      this.ticking = false;
      this.slider.addEventListener(
        'scroll',
        () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.setActive();
            this.ticking = false;
          });
        },
        { passive: true }
      );

      // Rotation changes slide widths; variant swaps re-render or reorder
      // the slide list. Either way the dot set is stale — rebuild it.
      new ResizeObserver(() => this.rebuild()).observe(this.slider);
      new MutationObserver(() => this.rebuild()).observe(this.slider, { childList: true });

      this.rebuild();
    }

    rebuild() {
      this.slides = Array.from(this.slider.querySelectorAll('.slider__slide')).filter(
        (slide) => slide.offsetWidth > 0
      );

      if (this.slides.length < 2) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      this.render();
      this.setActive();
    }

    render() {
      this.innerHTML = this.slides
        .map(
          (_, i) =>
            `<button type="button" class="gallery-dots__dot" aria-label="Go to image ${i + 1}"></button>`
        )
        .join('');

      this.dots = Array.from(this.children);

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.slider.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' });
        });
      });
    }

    setActive() {
      if (!this.dots || !this.slides || this.slides.length < 2) return;
      const scrollLeft = this.slider.scrollLeft;

      let index = 0;
      let closest = Infinity;

      for (let i = 0; i < this.slides.length; i++) {
        const distance = Math.abs(this.slides[i].offsetLeft - scrollLeft);
        if (distance < closest) {
          closest = distance;
          index = i;
        }
      }

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  customElements.define('gallery-dots', GalleryDots);
})();

/**
 * Mixed by Nasrin — in-view video playback
 * -----------------------------------------------------------------------
 * 2026-09-15: gallery videos are rendered by snippets/mixed-pdp-video.liquid
 * without the autoplay attribute (autoplay overrode preload="none" and
 * downloaded the 1080p file on every PDP visit). This plays each video
 * muted once its slide is at least half in view and pauses it when it
 * leaves. The markup has no controls attribute (iOS flashes its native
 * overlay on every start); controls are switched on only when autoplay is
 * unavailable: prefers-reduced-motion, or play() rejected. Source choice
 * (HLS first, MP4 fallback) is in the snippet.
 *
 * The lightbox (product-modal) renders its videos the same way. Opening it
 * pauses everything (Dawn ModalDialog.show calls pauseAllMedia); closing it
 * resumes the gallery videos that are still in view, since the observer
 * only fires on visibility changes.
 */
(function () {
  const videos = document.querySelectorAll('video[data-mixed-autoplay]');
  if (!videos.length || !('IntersectionObserver' in window)) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inView = new Set();

  function play(video) {
    if (reduceMotion) {
      video.controls = true;
      return;
    }
    const playing = video.play();
    if (playing && playing.catch) playing.catch(() => { video.controls = true; });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          inView.add(video);
          play(video);
        } else {
          inView.delete(video);
          if (!video.paused) video.pause();
        }
      });
    },
    { threshold: [0, 0.5] }
  );

  videos.forEach((video) => observer.observe(video));

  document.querySelectorAll('product-modal').forEach((modal) => {
    new MutationObserver(() => {
      if (modal.hasAttribute('open')) return;
      inView.forEach((video) => {
        if (!modal.contains(video) && video.paused) play(video);
      });
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  });
})();
